/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import dayjs, { type Dayjs } from '@/lib/dayjs';
import { countryCodes } from '@/constants/locale';
import { translateDayToSpanish } from '@/components/AZCustomComponent/Store/helpers';
import type { StoreDetails, DayType } from '@/types';

function isOpen24Hours({
  parsedClosingTime,
  parsedOpeningTime,
}: {
  parsedClosingTime: Dayjs;
  parsedOpeningTime: Dayjs;
}) {
  const FORMAT = 'H:m';
  const ALL_DAY_TIMESTAMP = '0:0';

  return (
    parsedClosingTime.format(FORMAT) === ALL_DAY_TIMESTAMP &&
    parsedOpeningTime.format(FORMAT) === ALL_DAY_TIMESTAMP
  );
}

export function determineOpenNowValue({
  storeDetails,
  locale,
}: {
  storeDetails: StoreDetails | undefined;
  locale: string;
}): boolean | null {
  // ATG returns `12:00 AM` as midnight value, whereas, YEXT returns `0:00`
  const midNightValue = '12:00 AM';
  const now = dayjs();
  // Get the end of the day (midnight)
  const endOfDay = now.endOf('day');

  const todayDayOfWeekInEnglish = dayjs().format('dddd');
  const todayDayOfWeekInSpanish = translateDayToSpanish(todayDayOfWeekInEnglish);
  const todayInLocaleLang =
    locale === countryCodes.mx ? todayDayOfWeekInSpanish : todayDayOfWeekInEnglish;

  const storeFullHoursObj = storeDetails?.storeFullHours;

  const storeFullHoursToday =
    storeFullHoursObj?.[todayInLocaleLang as DayType] ||
    storeFullHoursObj?.[todayDayOfWeekInEnglish as DayType];

  const END_OF_THE_DAY = locale === countryCodes.ptBr ? '23:59' : '11:59 PM';
  const openingTime = storeFullHoursToday?.open;
  const closingTime = ['Midnight', 'Medianoche'].includes(storeFullHoursToday?.close ?? '')
    ? END_OF_THE_DAY
    : storeFullHoursToday?.close;
  const storeClosedAllDay =
    openingTime === midNightValue && closingTime === midNightValue && !storeDetails?.open24Hours;
  const nowIsBeforeMidnight = closingTime === midNightValue && now.isBefore(endOfDay);
  const timeFormat = locale === countryCodes.ptBr ? 'HH:mm' : 'HH:mm A';
  const parsedClosingTime = dayjs(closingTime, timeFormat);
  const parsedOpeningTime = dayjs(openingTime, timeFormat);

  const isDuringBusinessHours =
    (now.isAfter(parsedOpeningTime) && nowIsBeforeMidnight) ||
    (now.isAfter(parsedOpeningTime) && now.isBefore(parsedClosingTime)) ||
    isOpen24Hours({ parsedClosingTime, parsedOpeningTime });

  // Yext API returns openedNow as true even if store is past business hours
  if (storeDetails?.openedNow && !isDuringBusinessHours) {
    return false;
  }

  if (storeClosedAllDay) {
    return false;
  }

  if (storeDetails?.open24Hours) {
    return true;
  }
  if (storeDetails?.openedNow) {
    return true;
  }
  // Yext API returns openedNow as false even if store is still opened
  if (!storeDetails?.openedNow) {
    if (isDuringBusinessHours) {
      return true;
    }
    return false;
  }
  return null;
}
